import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, MessageCircle, MapPin, Clock, Mail, CalendarPlus } from "lucide-react";
import { API } from "../context/OrderContext";
import { formatEuro, formatPickupDate, saveLastOrder } from "../lib/menu";
import { track } from "../lib/meta";

const PICKUP_ADDRESS = "Duke Ellingtonstraat #109, 4337XT, Middelburg, Zeeland, Nederland";

const buildOrderMessage = (order) => {
  const lines = [];
  lines.push("🍽️ De Zeeuwse Tori - Bestelbevestiging");
  lines.push("");
  lines.push(`Ordernummer: ${order.order_number}`);
  lines.push("");
  lines.push("Bestelling:");
  (order.items || []).forEach((i) => {
    lines.push(`• ${i.name} × ${i.portions} — ${formatEuro(i.line_total)}`);
  });
  if (order.cutlery > 0) {
    lines.push(`• Bestek × ${order.cutlery} — ${formatEuro(order.cutlery_total || 0)}`);
  }
  lines.push(`Totaal: ${formatEuro(order.total)}`);
  lines.push("");
  if (Array.isArray(order.pickups) && order.pickups.length > 0) {
    lines.push("Ophalen:");
    order.pickups.forEach((p) => {
      const prefix = order.pickups.length > 1 && p.category_label ? `${p.category_label}: ` : "";
      lines.push(`• ${prefix}${formatPickupDate(p.date)} · ${p.slot}`);
    });
  } else {
    const day = order.pickup_date ? formatPickupDate(order.pickup_date) : order.pickup_day;
    lines.push(`Ophalen: ${day} · ${order.time_slot}`);
  }
  lines.push(`Adres: ${PICKUP_ADDRESS}`);
  if (order.customer?.opmerkingen) {
    lines.push("");
    lines.push(`Speciale wensen: ${order.customer.opmerkingen}`);
  }
  lines.push("");
  lines.push("Bedankt voor je bestelling! 🧡");
  return lines.join("\n");
};

const icsEscape = (s) => String(s || "").replace(/\\/g, "\\\\").replace(/([,;])/g, "\\$1").replace(/\n/g, "\\n");

const downloadCalendar = (order) => {
  const pd = order.pickup_date;
  const slot = (order.time_slot || "").replace("\u2013", "-");
  const [startT, endT] = slot.split("-").map((s) => s.trim());
  if (!pd || !startT) return;
  const toDt = (t) => `${pd.replace(/-/g, "")}T${t.replace(":", "")}00`;
  const now = new Date();
  const p = (n) => String(n).padStart(2, "0");
  const stamp = `${now.getUTCFullYear()}${p(now.getUTCMonth() + 1)}${p(now.getUTCDate())}T${p(now.getUTCHours())}${p(now.getUTCMinutes())}${p(now.getUTCSeconds())}Z`;
  const desc = `Bestelling ${order.order_number}. ${(order.items || []).map((i) => `${i.name} x ${i.portions}`).join(", ")}. Totaal ${formatEuro(order.total)}.`;
  const ics = [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//De Zeeuwse Tori//NL", "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:${order.order_number}@zeeuwsetori.nl`,
    `DTSTAMP:${stamp}`,
    `DTSTART:${toDt(startT)}`,
    `DTEND:${toDt(endT || startT)}`,
    `SUMMARY:${icsEscape("Afhalen bij De Zeeuwse Tori - " + order.order_number)}`,
    `LOCATION:${icsEscape(PICKUP_ADDRESS)}`,
    `DESCRIPTION:${icsEscape(desc)}`,
    "BEGIN:VALARM", "TRIGGER:-PT1H", "ACTION:DISPLAY", "DESCRIPTION:Afhalen bij De Zeeuwse Tori", "END:VALARM",
    "END:VEVENT", "END:VCALENDAR",
  ].join("\r\n");
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `zeeuwse-tori-${order.order_number}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export default function Confirmation() {
  const [params] = useSearchParams();
  const orderNumber = params.get("order");
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [wa, setWa] = useState("31644582821");
  const purchaseTracked = React.useRef(false);

  useEffect(() => {
    axios.get(`${API}/config`).then((r) => setWa(r.data.whatsapp_number)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!orderNumber) return;
    let tries = 0;
    const load = () => {
      axios
        .get(`${API}/orders/${orderNumber}`)
        .then((r) => {
          setOrder(r.data);
          saveLastOrder(r.data);
          if (!purchaseTracked.current && r.data.payment_status === "paid") {
            purchaseTracked.current = true;
            track(
              "Purchase",
              {
                value: r.data.total,
                currency: "EUR",
                num_items: r.data.total_portions,
                content_ids: (r.data.items || []).map((i) => i.id),
                content_type: "product",
              },
              { eventID: r.data.order_number }
            );
          }
        })
        .catch(() => setNotFound(true));
    };
    load();
    const iv = setInterval(() => {
      tries += 1;
      if (tries > 5) return clearInterval(iv);
      load();
    }, 3000);
    return () => clearInterval(iv);
  }, [orderNumber]);

  if (notFound) {
    return <div className="max-w-[900px] mx-auto px-6 py-20 text-center font-[600] text-[#0D3B4C]">Bestelling niet gevonden.</div>;
  }
  if (!order) {
    return <div className="max-w-[900px] mx-auto px-6 py-20 text-center font-[600] text-[#0D3B4C]">Bezig met laden...</div>;
  }

  return (
    <div className="max-w-[900px] mx-auto w-full px-6 py-12" data-testid="confirmation-page">
      <div className="flex flex-col items-center text-center">
        <CheckCircle2 size={64} className="text-[#1B6B2E]" />
        <h1 className="text-4xl sm:text-5xl font-[700] text-[#0D3B4C] tracking-tight mt-4">Bedankt voor je bestelling!</h1>
        <p className="mt-3 text-lg md:text-xl font-[600] text-[#0D3B4C]">
          Ordernummer: <span data-testid="order-number">{order.order_number}</span>
        </p>
      </div>

      <div className="bg-[#E8F5E9] p-6 mt-10">
        <h2 className="text-xl font-[700] text-[#0D3B4C] mb-4">Overzicht</h2>
        <div className="space-y-2">
          {order.items.map((i) => (
            <div key={i.id} className="flex justify-between font-[600] text-[#0D3B4C]">
              <span>{i.name} × {i.portions}</span>
              <span>{formatEuro(i.line_total)}</span>
            </div>
          ))}
        </div>
        {order.cutlery > 0 && (
          <div className="flex justify-between font-[600] text-[#0D3B4C] mt-2" data-testid="confirmation-cutlery">
            <span>Bestek × {order.cutlery}</span>
            <span>{formatEuro(order.cutlery_total || 0)}</span>
          </div>
        )}
        {order.customer?.opmerkingen && (
          <div className="mt-3 text-sm text-[#0D3B4C]" data-testid="confirmation-notes">
            <span className="font-[700]">Speciale wensen:</span> {order.customer.opmerkingen}
          </div>
        )}
        <div className="border-t-2 border-white my-4" />
        <div className="flex justify-between text-2xl font-[700] text-[#0D3B4C]"><span>Totaal</span><span>{formatEuro(order.total)}</span></div>
        <p className="text-sm text-[#0D3B4C]">waarvan 9% BTW: {formatEuro(order.vat)}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
        <div className="border-2 border-[#E8F5E9] p-5 flex items-start gap-3">
          <Clock className="text-[#1B6B2E] flex-shrink-0" />
          <div>
            <p className="font-[600] text-[#0D3B4C]">Ophalen</p>
            {Array.isArray(order.pickups) && order.pickups.length > 0 ? (
              <div className="space-y-1" data-testid="confirmation-pickups">
                {order.pickups.map((p, i) => (
                  <p key={i} className="font-[400] text-[#0D3B4C]">
                    {p.category_label && order.pickups.length > 1 && <span className="font-[700]">{p.category_label}: </span>}
                    {formatPickupDate(p.date)} · {p.slot}
                  </p>
                ))}
              </div>
            ) : (
              <p className="font-[400] text-[#0D3B4C]">{order.pickup_date ? formatPickupDate(order.pickup_date) : order.pickup_day} · {order.time_slot}</p>
            )}
          </div>
        </div>
        <div className="border-2 border-[#E8F5E9] p-5 flex items-center gap-3">
          <MapPin className="text-[#1B6B2E]" />
          <div>
            <p className="font-[600] text-[#0D3B4C]">Adres</p>
            <p className="font-[400] text-[#0D3B4C]">Duke Ellingtonstraat #109, 4337XT, Middelburg, Zeeland, Nederland</p>
          </div>
        </div>
      </div>

      <div className="flex justify-center mt-6">
        <button
          type="button"
          onClick={() => downloadCalendar(order)}
          data-testid="confirmation-add-calendar"
          className="inline-flex items-center gap-2 border-2 border-[#0D3B4C] text-[#0D3B4C] font-[700] uppercase tracking-wider py-3 px-7 hover:bg-[#0D3B4C] hover:text-white transition-colors"
        >
          <CalendarPlus size={20} /> Voeg toe aan agenda
        </button>
      </div>

      {order.invoice_sent && (
        <div className="flex items-center justify-center gap-2 mt-8 bg-[#E8F5E9] text-[#1B6B2E] font-[600] py-3 px-4" data-testid="email-sent-line">
          <Mail size={18} /> Bevestiging verstuurd naar {order.customer?.email || "je e-mailadres"}
        </div>
      )}

      <p className="text-center font-[400] text-[#0D3B4C] mt-8">
        Je ontvangt een bevestiging per e-mail. Wil je de bestelling ook op WhatsApp bewaren? Stuur hem naar jezelf.
      </p>
      <div className="flex flex-col sm:flex-row justify-center items-center gap-3 mt-4">
        <a
          href={`https://wa.me/?text=${encodeURIComponent(buildOrderMessage(order))}`}
          target="_blank"
          rel="noreferrer"
          data-testid="confirmation-send-whatsapp"
          className="inline-flex items-center gap-2 bg-[#1B6B2E] text-white font-[700] uppercase tracking-wider py-4 px-8 hover:bg-[#144F22] transition-colors"
        >
          <MessageCircle size={20} /> Stuur bevestiging naar mijn WhatsApp
        </a>
        <a
          href={`https://wa.me/${wa}`}
          target="_blank"
          rel="noreferrer"
          data-testid="confirmation-whatsapp"
          className="inline-flex items-center gap-2 border-2 border-[#1B6B2E] text-[#1B6B2E] font-[700] uppercase tracking-wider py-4 px-8 hover:bg-[#E8F5E9] transition-colors"
        >
          <MessageCircle size={20} /> App ons met vragen
        </a>
      </div>
      <div className="text-center mt-8">
        <Link to="/" className="font-[600] text-[#1B6B2E] underline">Terug naar home</Link>
      </div>
    </div>
  );
}
